import { SportData } from '@/types/sports.model';
import { z } from 'zod';

const SURFACE_TRANSLATIONS: Record<string, string> = {
  ARTIFICIAL_GRASS_SAND: 'Césped Artificial con Arena',
  SYNTHETIC_RESIN: 'Resina Sintética',
  CONCRETE: 'Cemento',
  POROUS_CONCRETE: 'Cemento Poroso',
  GRASS: 'Césped',
  CLAY: 'Arcilla',
  HARD: 'Pista Dura',
};

const WALL_TYPE_TRANSLATIONS: Record<string, string> = {
  GLASS: 'Vidrio',
  CONCRETE: 'Cemento',
  NONE: 'Sin Pared',
};

export const createSurfaceMap = (
  availableSurfaces: string[],
): Record<string, string> => {
  const map: Record<string, string> = {};
  availableSurfaces.forEach((surface) => {
    map[surface] = SURFACE_TRANSLATIONS[surface] || surface;
  });
  return map;
};

export const createWallTypeMap = (
  availableWallTypes: string[],
): Record<string, string> => {
  const map: Record<string, string> = {};
  availableWallTypes.forEach((wallType) => {
    map[wallType] = WALL_TYPE_TRANSLATIONS[wallType] || wallType;
  });
  return map;
};

export const currencySchema = z.object({
  isoCode: z.string().min(1, 'Debe ingresar un código de moneda'),
  name: z.string().min(1, 'Debe ingresar un nombre de moneda'),
});

export const priceSchema = z.object({
  value: z
    .number({
      required_error: 'Debe ingresar un precio para la cancha',
      invalid_type_error: 'Debe ingresar un precio para la cancha',
    })
    .positive('El precio debe ser mayor a 0'),
  currency: currencySchema,
});

export const sportSchema = z.object({
  name: z.string().min(1, 'Debe ingresar un nombre de deporte'),
  uid: z.string(),
});

export const createDynamicCourtSchema = (sportData?: SportData) => {
  const surfaceSchema =
    sportData?.data?.court?.surface && sportData.data.court.surface.length > 0
      ? z.enum([
          sportData.data.court.surface[0],
          ...sportData.data.court.surface.slice(1),
        ] as [string, ...string[]])
      : z.string().optional();

  const wallTypeSchema =
    sportData?.data?.court?.wallType && sportData.data.court.wallType.length > 0
      ? z.enum([
          sportData.data.court.wallType[0],
          ...sportData.data.court.wallType.slice(1),
        ] as [string, ...string[]])
      : z.string().optional();

  const baseSchema = {
    sport: sportSchema,
    name: z.string().min(1, 'Debe ingresar un nombre para la cancha'),
    surface: surfaceSchema,
    price: priceSchema,
    clubId: z.string().default(''),
    hasRoof: z.boolean().default(true),
  };

  if (
    sportData?.data?.court?.wallType &&
    sportData.data.court.wallType.length > 0
  ) {
    return z.object({
      ...baseSchema,
      wallType: wallTypeSchema,
    });
  }

  return z.object(baseSchema);
};

export type CourtFormData = z.infer<
  ReturnType<typeof createDynamicCourtSchema>
>;

export const surfaceMap: Record<string, string> = SURFACE_TRANSLATIONS;
export const wallTypeMap: Record<string, string> = WALL_TYPE_TRANSLATIONS;
